import { useRef, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import ListGroup from 'react-bootstrap/ListGroup'
import Row from 'react-bootstrap/Row'
import Spinner from 'react-bootstrap/Spinner'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { translateReceipt, uploadReceipt, type GroupDetail, type Receipt, type Transaction } from '../../../../types'
import { actionApiCall } from '../../api/actionApiCall'
import { useCapabilities } from '../../hooks/useCapabilities'
import { useCurrencies } from '../../hooks/useCurrencies'
import { useGroup } from '../../hooks/useGroups'
import { useInvalidateReceipts, useReceipts, useTransaction } from '../../hooks/useReceipts'
import { useOnlineStatus } from '../../offline/useOnlineStatus'
import { formatDateTime } from '../../utils/format'
import { exponentOf, formatMoney } from '../../utils/money'

/**
 * Jde útratu upravovat na stránce účtenky (fotka vedle údajů, položky)?
 * Jen útratu rozdělenou podle položek nebo rovným dílem - přesné částky
 * a další způsoby dělení umí jen formulář pohybu.
 */
export function lzeUpravitPodleUctenky(transaction: Transaction): boolean {
    return transaction.type === 'EXPENSE' && transaction.receiptCount > 0 && (transaction.splitMode === 'ITEMS' || transaction.splitMode === 'EQUAL')
}

/** Adresa úpravy útraty z účtenky - stránka si účtenku dohledá sama. */
export function adresaUpravyUtraty(groupId: string, transactionId: string): string {
    return `/skupiny/${groupId}/pohyby/${transactionId}/upravit`
}

/**
 * Úprava útraty z účtenky: dohledá účtenku útraty a přesměruje na její
 * stránku - tatáž stránka slouží ke kontrole před potvrzením i k úpravě.
 */
export function UpravaUtratyZUctenky() {
    const { groupId = '', transactionId = '' } = useParams<{ groupId: string; transactionId: string }>()
    const { data: receipts, isLoading } = useReceipts(transactionId)

    if (isLoading) {
        return <Spinner animation="border" />
    }
    const receipt = receipts?.[0]
    if (!receipt) {
        return <Navigate to={`/skupiny/${groupId}/pohyby/${transactionId}/uctenka`} replace />
    }
    return <Navigate to={`/skupiny/${groupId}/uctenky/${receipt.id}`} replace />
}

/**
 * Útrata s účtenkou jen ke čtení - fotka, údaje útraty, kdo kolik platí
 * a položky i s tím, kdo co měl. Upravuje se na stránce účtenky (tlačítko
 * Upravit). Útrata bez účtenky tu účtenku dostane nahráním fotky.
 */
export function ReceiptPage() {
    const { groupId = '', transactionId = '' } = useParams<{ groupId: string; transactionId: string }>()
    const navigate = useNavigate()
    const online = useOnlineStatus()

    const { data: currencies } = useCurrencies()
    const { data: group } = useGroup(groupId)
    const { data: transaction, isLoading: transactionLoading } = useTransaction(transactionId)
    const { data: receipts, isLoading: receiptsLoading } = useReceipts(transactionId)
    const invalidate = useInvalidateReceipts()

    if (transactionLoading || receiptsLoading) {
        return <Spinner animation="border" />
    }

    if (!transaction || !group) {
        return <Alert variant="warning">Pohyb nebyl nalezen nebo k němu nemáte přístup.</Alert>
    }

    const receipt = receipts?.[0]
    const exponent = exponentOf(currencies, transaction.currencyCode)
    const jmeno = (memberId: string) => group.members.find((member) => member.id === memberId)?.displayName ?? 'Neznámý'

    return (
        <>
            <div className="mb-3">
                <Link to={`/skupiny/${groupId}`} className="small">
                    <i className="bi bi-arrow-left me-1" aria-hidden="true" />
                    Zpět na skupinu
                </Link>
                <div className="d-flex align-items-start justify-content-between gap-2 mt-2 flex-wrap">
                    <div className="min-w-0">
                        <h1 className="h4 mb-1">{transaction.title}</h1>
                        <div className="text-secondary">
                            {formatMoney(transaction.amountTotal, transaction.currencyCode, exponent)}
                            {transaction.currencyCode !== group.baseCurrencyCode &&
                                ` (${formatMoney(transaction.amountTotalBase, group.baseCurrencyCode, exponentOf(currencies, group.baseCurrencyCode))})`}
                            {' · '}
                            {formatDateTime(transaction.occurredAt)}
                        </div>
                    </div>
                    {receipt && lzeUpravitPodleUctenky(transaction) && (
                        <Button className="su-tappable" disabled={!online} onClick={() => navigate(`/skupiny/${groupId}/uctenky/${receipt.id}`)}>
                            <i className="bi bi-pencil me-1" aria-hidden="true" />
                            Upravit
                        </Button>
                    )}
                </div>
            </div>

            {!online && (
                <Alert variant="secondary" className="py-2 small">
                    <i className="bi bi-wifi-off me-1" aria-hidden="true" />
                    Offline — útratu teď nelze upravovat.
                </Alert>
            )}

            <Row className="g-3">
                <Col lg={5}>
                    {receipt ? (
                        <FotkaUctenky receipt={receipt} canWrite={online} onChanged={() => invalidate(transactionId)} />
                    ) : (
                        <NahraniUctenky transactionId={transactionId} canWrite={online} onUploaded={() => invalidate(transactionId)} />
                    )}
                </Col>
                <Col lg={7}>
                    <Card className="mb-3" data-testid="rozdeleni-utraty">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <span>Zaplatil {transaction.payers.map((payer) => jmeno(payer.memberId)).join(', ')}</span>
                            <span className="small text-secondary">{popisDeleni(transaction.splitMode)}</span>
                        </Card.Header>
                        <ListGroup variant="flush">
                            {transaction.shares.map((share) => (
                                <ListGroup.Item key={share.memberId} className="d-flex justify-content-between">
                                    <span>{jmeno(share.memberId)}</span>
                                    <span className="fw-semibold">{formatMoney(share.amount, transaction.currencyCode, exponent)}</span>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </Card>

                    {receipt && <PolozkyUctenky receipt={receipt} transaction={transaction} group={group} />}

                    {transaction.note && (
                        <Card body className="mb-3 small">
                            <span className="text-secondary">Poznámka: </span>
                            {transaction.note}
                        </Card>
                    )}

                    {receipt && !lzeUpravitPodleUctenky(transaction) && transaction.type === 'EXPENSE' && (
                        <div className="small text-secondary">
                            Útrata je rozdělená přesnými částkami — upravíte ji v historii skupiny přes Upravit.
                        </div>
                    )}
                </Col>
            </Row>
        </>
    )
}

function popisDeleni(splitMode: Transaction['splitMode']): string {
    switch (splitMode) {
        case 'ITEMS':
            return 'podle položek'
        case 'EQUAL':
            return 'rovným dílem'
        default:
            return 'přesnými částkami'
    }
}

/** Položky účtenky jen ke čtení - kdo co měl. */
function PolozkyUctenky({ receipt, transaction, group }: { receipt: Receipt; transaction: Transaction; group: GroupDetail }) {
    const { data: currencies } = useCurrencies()
    if (receipt.items.length === 0) {
        return null
    }

    const mena = receipt.detectedCurrencyCode ?? transaction.currencyCode
    const exponent = exponentOf(currencies, mena)
    const jmeno = (memberId: string) => group.members.find((member) => member.id === memberId)?.displayName ?? 'Neznámý'
    const rozdil = mena === transaction.currencyCode ? receipt.itemsTotal - transaction.amountTotal : 0

    return (
        <Card className="mb-3" data-testid="polozky-utraty">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <span>Položky</span>
                <span className="small text-secondary">součet {formatMoney(receipt.itemsTotal, mena, exponent)}</span>
            </Card.Header>
            <ListGroup variant="flush">
                {receipt.items.map((item) => (
                    <ListGroup.Item key={item.id}>
                        <div className="d-flex justify-content-between gap-2">
                            <span className="text-break">
                                {item.quantity !== 1 && <span className="text-secondary">{String(item.quantity).replace('.', ',')}× </span>}
                                {item.label}
                            </span>
                            <span className="text-nowrap">{formatMoney(item.totalPrice, mena, exponent)}</span>
                        </div>
                        <div className="d-flex gap-1 flex-wrap mt-1">
                            {item.memberIds.length > 0 ? (
                                item.memberIds.map((memberId) => (
                                    <Badge key={memberId} bg="secondary-subtle" text="secondary-emphasis" className="fw-normal">
                                        {jmeno(memberId)}
                                    </Badge>
                                ))
                            ) : (
                                <span className="small text-secondary">nikomu nepřiřazeno</span>
                            )}
                        </div>
                    </ListGroup.Item>
                ))}
            </ListGroup>
            {rozdil !== 0 && (
                <Card.Footer className="small text-secondary">
                    Útrata je {formatMoney(transaction.amountTotal, transaction.currencyCode, exponent)}, položky o{' '}
                    {formatMoney(Math.abs(rozdil), mena, exponent)} {rozdil > 0 ? 'víc' : 'míň'} — sleva, spropitné nebo zaokrouhlení
                    se rozpočítalo poměrně.
                </Card.Footer>
            )}
        </Card>
    )
}

type FotkaUctenkyProps = {
    receipt: Receipt
    canWrite: boolean
    onChanged: () => Promise<void>
}

/** Fotka účtenky - po klepnutí v plné velikosti, dlouhá účtenka po stranách, případně přeložená. */
function FotkaUctenky({ receipt, canWrite, onChanged }: FotkaUctenkyProps) {
    const { data: capabilities } = useCapabilities()
    const [velka, setVelka] = useState(false)
    const [fotka, setFotka] = useState(1)
    // Který jazyk se zrovna zobrazuje; prázdná hodnota znamená originál.
    const [jazyk, setJazyk] = useState('')
    const [prekladam, setPrekladam] = useState(false)
    const cislo = Math.min(fotka, receipt.pageCount)

    const prelozit = async (targetLang: 'cs' | 'en') => {
        setPrekladam(true)
        try {
            const vysledek = await actionApiCall(() => translateReceipt({ path: { receiptId: receipt.id }, body: { targetLang } }), {
                successMessage: 'Účtenka byla přeložena.',
            })
            if (vysledek) {
                setJazyk(targetLang)
                await onChanged()
            }
        } finally {
            setPrekladam(false)
        }
    }

    // Fotka se odkazuje přímo URL - přes fetch by se musela převádět na blob.
    const zdroj = jazyk
        ? `/api/receipts/${receipt.id}/translation/${jazyk}/file`
        : `/api/receipts/${receipt.id}/file?variant=${velka ? 'full' : 'thumb'}&page=${cislo}`
    const preklady = receipt.translations ?? []

    return (
        <Card className="sticky-lg-top" style={{ top: '1rem' }}>
            <button
                type="button"
                className="btn p-0 border-0"
                onClick={() => setVelka((soucasna) => !soucasna)}
                aria-label={velka ? 'Zmenšit účtenku' : 'Zvětšit účtenku'}
            >
                <img
                    src={zdroj}
                    alt={jazyk ? 'Přeložená účtenka' : 'Fotografie účtenky'}
                    className="img-fluid rounded-top"
                    style={{ maxHeight: velka ? 'none' : '24rem', objectFit: 'contain', width: '100%' }}
                />
            </button>
            <Card.Footer className="d-flex flex-wrap align-items-center gap-2">
                <a href={`/api/receipts/${receipt.id}/file?page=${cislo}`} target="_blank" rel="noreferrer" className="small me-auto">
                    <i className="bi bi-box-arrow-up-right me-1" aria-hidden="true" />
                    Otevřít celou
                </a>
                {receipt.pageCount > 1 && (
                    <div className="btn-group btn-group-sm" role="group" aria-label="Fotky účtenky">
                        {Array.from({ length: receipt.pageCount }, (_, index) => index + 1).map((strana) => (
                            <button
                                key={strana}
                                type="button"
                                className={`btn su-tappable ${jazyk === '' && strana === cislo ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                aria-label={`Fotka ${strana} z ${receipt.pageCount}`}
                                onClick={() => {
                                    setJazyk('')
                                    setFotka(strana)
                                }}
                            >
                                {strana}
                            </button>
                        ))}
                    </div>
                )}
                {preklady.length > 0 && (
                    <div className="btn-group btn-group-sm" role="group" aria-label="Zobrazená verze účtenky">
                        <button type="button" className={`btn ${jazyk === '' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setJazyk('')}>
                            Originál
                        </button>
                        {preklady.map((preklad) => (
                            <button
                                key={preklad.targetLang}
                                type="button"
                                className={`btn ${jazyk === preklad.targetLang ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                onClick={() => setJazyk(preklad.targetLang)}
                            >
                                {preklad.targetLang === 'cs' ? 'Česky' : 'Anglicky'}
                            </button>
                        ))}
                    </div>
                )}
            </Card.Footer>
            {capabilities?.receiptTranslation && (
                <Card.Footer className="d-flex flex-wrap align-items-center gap-2">
                    <span className="small text-secondary me-auto">Přeložit fotku{receipt.pageCount > 1 ? ' (první)' : ''}:</span>
                    <Button variant="outline-primary" size="sm" className="su-tappable" disabled={!canWrite || prekladam} onClick={() => void prelozit('cs')}>
                        {prekladam && <Spinner animation="border" size="sm" className="me-1" />}
                        Do češtiny
                    </Button>
                    <Button variant="outline-primary" size="sm" className="su-tappable" disabled={!canWrite || prekladam} onClick={() => void prelozit('en')}>
                        Do angličtiny
                    </Button>
                </Card.Footer>
            )}
        </Card>
    )
}

type NahraniUctenkyProps = {
    transactionId: string
    canWrite: boolean
    onUploaded: () => Promise<void>
}

/** Útrata bez účtenky - účtenka se k ní dá dodatečně vyfotit. */
function NahraniUctenky({ transactionId, canWrite, onUploaded }: NahraniUctenkyProps) {
    const fileInput = useRef<HTMLInputElement>(null)
    const [nahravam, setNahravam] = useState(false)

    const nahrat = async (file: File) => {
        setNahravam(true)
        try {
            const vysledek = await actionApiCall(() => uploadReceipt({ path: { transactionId }, body: { file } }), {
                successMessage: 'Účtenka byla nahrána.',
            })
            if (vysledek) {
                await onUploaded()
            }
        } finally {
            setNahravam(false)
        }
    }

    return (
        <Card body className="text-center">
            <p className="text-secondary">K tomuto pohybu zatím není nahraná žádná účtenka.</p>
            <input
                ref={fileInput}
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={(event) => {
                    const file = event.target.files?.[0]
                    if (file) {
                        void nahrat(file)
                    }
                    event.target.value = ''
                }}
            />
            <Button className="su-tappable" disabled={!canWrite || nahravam} onClick={() => fileInput.current?.click()}>
                {nahravam ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-camera me-1" aria-hidden="true" />}
                Vyfotit nebo vybrat účtenku
            </Button>
        </Card>
    )
}
